import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { decryptSlug } from "../../../util/encryption";
import Loader from "../../common/Loader";
import axios from "axios";
import { baseUrl, localserver, ROUTE_BASE_URL } from "../../../api/common";
import { showToast, ToastMessage } from "../../Shared/ToastMessage";

const ArrowLeftIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m12 19-7-7 7-7"></path>
    <path d="M19 12H5"></path>
  </svg>
);

const DeleteIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18"></path>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

export default function CheckInDetails() {
  const { id, checkinId } = useParams();
  const history = useHistory();
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [checkInLogs, setCheckInLogs] = useState([]);

  const [attendee, setAttendee] = useState(null);
  const [getAttendeeDetailsStatus, setGetAttendeeDetailsStatus] = useState(false);

  const formatDateTime = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const loadAttendeeData = async () => {
    setGetAttendeeDetailsStatus(true);
    try {
      const attendeeParams = {
        event_id: localStorage.getItem("eventId"),
        check_id: decryptSlug(`${checkinId}`),
        attendee_id: decryptSlug(`${id}`),
      };
      const [attendeeRes, logsRes] = await Promise.all([
        axios.post(`${localserver}/getcheckin_attendee`, attendeeParams, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }),
        axios.post(`${localserver}/getcheckin_log`, attendeeParams, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }),
      ]);

      if (attendeeRes.data.success && attendeeRes.data.data.length > 0) {
        const apiData = attendeeRes.data.data[0];
        const checkinData = attendeeRes.data.checkin || [];
        const entryLimit = checkinData[0]
          ? parseInt(checkinData[0].entry_limit) || 1
          : 1;

        const logs = logsRes.data.data || [];
        const sortedLogs = logs.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setCheckInLogs(sortedLogs);

        const checkIns = sortedLogs.map((item) => ({
          id: item.id,
          timestamp: formatDateTime(item.created_at),
        }));

        const transformedAttendee = {
          id: apiData.attendee_id,
          name: `${apiData.first_name} ${apiData.last_name}`.trim(),
          email: apiData.email,
          ticketRef: apiData.ticket_id.toString(),
          ticketType: apiData.ticket_name,
          checkedIn: checkIns.length > 0,
          checkIns,
          checkInDate:
            checkIns.length > 0 ? checkIns[0].timestamp.split(" ")[0] : null,
          checkInTime:
            checkIns.length > 0 ? checkIns[0].timestamp.split(" ")[1] : null,
          entryLimit,
          currentCount: checkIns.length,
        };
        setAttendee(transformedAttendee);
      } else {
        setAttendee(null);
        setCheckInLogs([]);
        console.error("Error: Failed to fetch user data");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setAttendee(null);
      setCheckInLogs([]);
    } finally {
      setGetAttendeeDetailsStatus(false);
    }
  };

  const handleCheckIn = async () => {
    if (attendee.currentCount >= attendee.entryLimit) {
      showToast("Maximum entry limit reached", "error");
      return;
    }

    const userId = localStorage.getItem("loginUserId");
    if (!userId) {
      console.error("User ID not found");
      return;
    }
    try {
      const response = await axios.post(
        `${localserver}/checkin_add`,
        {
          user_id: parseInt(userId),
          event_id: parseInt(localStorage.getItem("eventId")),
          check_id: parseInt(decryptSlug(`${checkinId}`)),
          attendee_id: parseInt(decryptSlug(`${id}`)),
          ticket_id: parseInt(attendee.ticketRef),
          attendee_in: 1,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      if (response.data.success) {
        showToast(response.data.message, "success");
        loadAttendeeData();
        console.log("Check-in added successfully");
      } else {
        showToast(response.data.message, "error");
        console.error("Failed to add check-in:", response.data.message);
      }
    } catch (error) {
      console.error("Error adding check-in:", error);
    }
  };

  const handleRemoveCheckIn = async (checkInId) => {
    try {
      const response = await axios.post(
        `${localserver}/checkin_log_delete`,
        {
          id: parseInt(checkInId),
          event_id: parseInt(localStorage.getItem("eventId")),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      if (response.data.success) {
        showToast(response.data.message, "success");
        loadAttendeeData();
        console.log(`Check-in ${checkInId} deleted successfully`);
      } else {
        showToast(response.data.message, "error");
        console.error("Failed to delete check-in:", response.data.message);
      }
    } catch (error) {
      console.error("Error deleting check-in:", error);
    }
    setShowCheckInModal(false);
  };

  useEffect(() => {
    loadAttendeeData();
  }, []);

  const handleViewMore = () => {
    setShowCheckInModal(true);
  };

  const getInitials = (name) => {
    return name.charAt(0).toUpperCase();
  };

  const renderCheckInModal = () => {
    if (!showCheckInModal) return null;

    return (
      <>
        {/* Overlay */}
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
          onClick={() => setShowCheckInModal(false)}
        >
          {/* Modal Content */}
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "400px",
              maxHeight: "80vh",
              overflow: "hidden",
              borderLeft: "4px solid #8BC34A",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                backgroundColor: "#6B7280",
                color: "#ffffff",
                padding: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h2
                style={{
                  fontSize: "16px",
                  fontWeight: "500",
                  margin: 0,
                }}
              >
                Check-in Details
              </h2>
              <button
                onClick={() => setShowCheckInModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#ffffff",
                  fontSize: "20px",
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "4px",
                  transition: "background-color 0.2s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = "transparent";
                }}
              >
                <i className="fa fa-times"></i>
              </button>
            </div>

            {/* Body */}
            <div
              style={{
                padding: "16px",
                maxHeight: "300px",
                overflowY: "auto",
              }}
            >
              {checkInLogs.length > 0 ? (
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                  }}
                >
                  {checkInLogs.map((log) => (
                    <li
                      key={log.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px 0",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#1a1a1a",
                          }}
                        >
                          <strong>{log.checkin_name}</strong> -{" "}
                          {formatDateTime(log.created_at)}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#666666",
                            marginTop: "4px",
                          }}
                        >
                          Status: {log.checkin_status}
                        </div>
                        {log.ticket_ids && (
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#666666",
                              marginTop: "2px",
                            }}
                          >
                            Tickets: {log.ticket_ids}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveCheckIn(log.id)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#EF4444",
                          cursor: "pointer",
                          padding: "4px",
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          transition: "background-color 0.2s ease",
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = "#FEF2F2";
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = "transparent";
                        }}
                      >
                        <DeleteIcon />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    color: "#666666",
                    fontSize: "14px",
                    padding: "24px",
                  }}
                >
                  No check-in logs found
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  const NotFound = () => (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f9fafb",
        padding: "24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#666666",
      }}
    >
      <div
        style={{
          textAlign: "center",
          padding: "60px 20px",
        }}
      >
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>
          <i className="fa fa-user-o"></i>
        </div>
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "600",
            marginBottom: "8px",
            color: "#1a1a1a",
          }}
        >
          Attendee not found
        </h3>
      </div>
    </div>
  );

  const MainContent = () => (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f9fafb",
        padding: "24px",
      }}
    >
      <header
        style={{
          backgroundColor: "#455A64",
          color: "#ffffff",
          position: "relative",
          borderRadius: "12px 12px 0 0",
          overflow: "hidden",
        }}
      >
        <button
          onClick={() => history.goBack()}
          style={{
            position: "absolute",
            left: "16px",
            top: "16px",
            backgroundColor: "transparent",
            border: "none",
            color: "#ffffff",
            padding: "8px",
            borderRadius: "8px",
            cursor: "pointer",
            transition: "background-color 0.2s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = "transparent";
          }}
        >
          <ArrowLeftIcon />
        </button>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "32px 24px",
          }}
        >
          <div
            style={{
              width: "96px",
              height: "96px",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderRadius: "50%",
              marginBottom: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
            }}
          >
            <div
              style={{
                fontSize: "24px",
                fontWeight: "500",
                color: "#666666",
              }}
            >
              {getInitials(attendee.name)}
            </div>
          </div>
          <h1
            style={{
              fontSize: "20px",
              fontWeight: "500",
              marginBottom: "4px",
            }}
          >
            {attendee.name}
          </h1>
          <p
            style={{
              fontSize: "14px",
              color: "rgba(255, 255, 255, 0.9)",
            }}
          >
            {attendee.email}
          </p>
        </div>
      </header>
      <div
        style={{
          backgroundColor: "#ffffff",
          padding: "24px",
          margin: "16px",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "14px",
                color: "#666666",
                marginBottom: "4px",
              }}
            >
              Attendee Ref.
            </div>
            <div
              style={{
                fontSize: "16px",
                color: "#1a1a1a",
              }}
            >
              #{attendee.id}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: "14px",
                color: "#666666",
                marginBottom: "4px",
              }}
            >
              Ticket Name:
            </div>
            <div
              style={{
                fontSize: "16px",
                color: "#1a1a1a",
              }}
            >
              {attendee.ticketType}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: "14px",
                color: "#666666",
                marginBottom: "4px",
              }}
            >
              Check-ins
            </div>
            {attendee.currentCount > 0 && (
              <div
                style={{
                  fontSize: "16px",
                  color: "#1a1a1a",
                }}
              >
                Latest: {attendee.checkInDate} {attendee.checkInTime}
              </div>
            )}
            <div
              style={{
                fontSize: "14px",
                color: "#666666",
                marginTop: "4px",
              }}
            >
              Used: {attendee.currentCount} / {attendee.entryLimit}
            </div>
            {attendee.currentCount < attendee.entryLimit && (
              <button
                onClick={handleCheckIn}
                style={{
                  marginTop: "8px",
                  padding: "8px 16px",
                  backgroundColor: "#8BC34A",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  transition: "background-color 0.2s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = "#7CB342";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = "#8BC34A";
                }}
              >
                Check In Now
              </button>
            )}
            {attendee.currentCount > 0 && (
              <button
                onClick={handleViewMore}
                style={{
                  fontSize: "14px",
                  color: "#666666",
                  textDecoration: "underline",
                  marginTop: "8px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  transition: "color 0.2s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.color = "#184778";
                }}
                onMouseOut={(e) => {
                  e.target.style.color = "#666666";
                }}
              >
                View check-in history
              </button>
            )}
          </div>
        </div>
      </div>
      {attendee.currentCount > 0 && (
        <div
          style={{
            margin: "0 16px 16px 16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#8BC34A",
              color: "#ffffff",
              textAlign: "center",
              padding: "16px",
              borderRadius: "8px",
              fontWeight: "500",
              fontSize: "18px",
            }}
          >
            {attendee.name} last checked in {"@"} {attendee.checkInTime}
          </div>
        </div>
      )}
    </div>
  );

  let content;
  if (getAttendeeDetailsStatus) {
    content = <Loader />;
  } else if (!attendee) {
    content = <NotFound />;
  } else {
    content = <MainContent />;
  }

  return (
    <>
      <ToastMessage />
      {content}
      {renderCheckInModal()}
    </>
  );
}